import React, { useCallback, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import ExcelJS from "exceljs";
import { useDispatch } from "react-redux";
import { ImCancelCircle } from "react-icons/im";
import { prepareWorkSheetFromCSVFilePapaParser } from "../utils";
import { setInputFile, clearInputFile } from "../store/workspaceSlice";

const FileUpload = ({ onFileUpload }) => {
  const dispatch = useDispatch();
  const [parsedCSV, setParsedCSV] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFileName, setUploadedFileName] = useState(null);

  const resetFile = () => {
    setUploadedFileName("");
    dispatch(clearInputFile());
  };
  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];

        // Reset states for new upload
        setUploading(true);
        setUploadProgress(0);
        setUploadedFileName(null);

        const reader = new FileReader();
        if (file.type === "text/csv" || file.name.endsWith(".csv")) {
          console.log("Processing CSV file...");

          reader.onload = async (e) => {
            const csvData = e.target.result; // Get the text content of the file
            // Pass the file and its content to the parent handler
            await onFileUpload(file, csvData)
              .then(() => {
                setUploadProgress(100);
                setUploadedFileName(file.name);
              })
              .catch(() => {
                console.error("File upload failed");
              })
              .finally(() => {
                setUploading(false);
              });

            // Create a new workbook
            const wb = new ExcelJS.Workbook();
            let worksheet = wb.addWorksheet("inputsheet");
            // Loading csv data into excel sheet with PapaParser
            worksheet = prepareWorkSheetFromCSVFilePapaParser(
              csvData,
              worksheet,
              setParsedCSV,
            );
          };
        } else if (
          file.type ===
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
          file.name.endsWith(".xlsx")
        ) {
          console.log(
            "The input file is already an Excel file. No conversion needed.",
          );
          // Load the Excel file into the workbook
          const wb = new ExcelJS.Workbook();
          await wb.xlsx.load(await file.arrayBuffer());
          // Pass the file and its content to the parent handler
          await onFileUpload(file)
            .then(() => {
              setUploadProgress(100);
              setUploadedFileName(file.name);
            })
            .catch(() => {
              console.error("File upload failed");
            })
            .finally(() => {
              setUploading(false);
            });

          let worksheet = wb.getWorksheet(1);
          // Extract rows and handle objects, including Date
          let maxColumns = 0;
          worksheet.eachRow((row) => {
            maxColumns = Math.max(maxColumns, row.cellCount);
          });

          // Extract rows with empty cell handling
          const extractedRows = [];
          worksheet.eachRow((row) => {
            const plainValues = Array.from(
              { length: maxColumns },
              (_, colIndex) => {
                const cell = row.getCell(colIndex + 1).value; // 1-based index
                if (cell instanceof Date) {
                  return cell.toLocaleDateString(); // Format Date
                }
                if (cell && typeof cell === "object" && "result" in cell) {
                  return cell.result; // Handle formulas
                }
                return cell ?? ""; // Empty cells are filled with an empty string
              },
            );
            extractedRows.push(plainValues);
          });
          setParsedCSV(extractedRows);

          console.log("Excel workbook loaded for further processing.");
        } else {
          console.error(
            "Unsupported file type. Please upload a CSV or Excel file.",
          );
        }
        reader.readAsText(file); // For CSV or text files
        // reader.readAsArrayBuffer(file); // For binary files like Excel
      }
    },
    [onFileUpload],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
    },
    maxFiles: 1, // Restrict to one file at a time
  });
  // Updating file in Redux
  useEffect(() => {
    if (parsedCSV?.length > 0) {
      dispatch(setInputFile(parsedCSV));
    }
  }, [parsedCSV, dispatch]);

  return (
    <div className="p-6 border-dashed border rounded-md text-center">
      <div
        {...getRootProps()}
        className={`border-dashed border-2 p-6 rounded-md text-center ${
          isDragActive ? "border-blue-500" : "border-gray-300"
        }`}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>Drop the file here...</p>
        ) : (
          <p>Drag and drop a file here, or click to select one</p>
        )}
      </div>

      {uploading && (
        <div className="mt-4">
          <p>Uploading...</p>
          <div className="w-full bg-gray-200 rounded h-4">
            <div
              className="bg-blue-500 h-4 rounded"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        </div>
      )}

      {uploadedFileName && (
        <div className="mt-4">
          <div className="flex justify-between items-center bg-gray-200 p-2 rounded-md">
            <p className="text-green-600">{uploadedFileName}</p>
            <span
              onClick={resetFile}
              className="text-red-500 text-lg hover:cursor-pointer hover:text-red-600 active:text-red-700"
            >
              <ImCancelCircle />
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
